const CAP18 = {
  id: 18,
  slug: "cap18-hashing-y-firmas-digitales-3",
  title: "Capítulo 18 — El hashing y las firmas digitales (II)",
  sections: [
    {
      heading: "Otras aplicaciones populares del hashing",
      text: [
        "Hasta ahora hemos visto cómo el hashing es clave para la seguridad en blockchain y la creación de firmas digitales. Pero su uso va mucho más allá.",
        "El hashing también se utiliza en criptomonedas, gestión de datos, ciberseguridad, protección de contraseñas y detección de archivos manipulados o maliciosos.",
        "Veamos ahora estas aplicaciones en la vida real, explicadas de forma sencilla."
      ]
    },

    {
      heading: "1. Hashing en criptomonedas",
      text: [
        "En blockchain, el hashing tiene una función vital: **proteger la integridad de las transacciones y mantener la cadena inmutable**.",
        "Cada transacción se agrupa dentro de un bloque, y **cada bloque contiene el hash del bloque anterior**. Esto crea una estructura tipo eslabones de una cadena. Si alguien modifica un bloque, el hash cambia, y toda la cadena pierde validez.",
        "Esto dificulta enormemente que alguien pueda manipular la blockchain, ya que tendría que modificar varios bloques en cientos o miles de nodos al mismo tiempo."
      ],
      list: [
        "🔹 Los hashes permiten enlazar bloques y detectar manipulaciones.",
        "🔹 Aseguran la transparencia y verificabilidad de las transacciones.",
        "🔹 Ayudan a mantener la descentralización y la confianza entre los participantes.",
        "🔹 Son la base de mecanismos como Proof of Work (PoW) y Merkle Trees."
      ]
    },

    {
      heading: "2. Hashing en la gestión de datos",
      text: [
        "El hashing también se utiliza para comprobar la **integridad de archivos, documentos y bases de datos**.",
        "Al guardar un archivo, se puede generar un hash único. Luego, si alguien intenta modificar, editar o reemplazar el archivo, el hash cambiará automáticamente.",
        "Esto permite identificar si un documento ha sido alterado, incluso si el cambio ha sido mínimo."
      ],
      list: [
        "🔹 Se usa para verificar que archivos grandes no han sido modificados.",
        "🔹 Útil en copias de seguridad (backups) y almacenamiento de datos sensibles.",
        "🔹 Se aplica para detectar cambios sospechosos en documentos legales, médicos o financieros.",
        "🔹 Es clave en la sincronización de datos entre servidores."
      ]
    },

    {
      heading: "Ejemplo visual",
      pre: `Archivo original       —> Hash: 8d0f45a3c786...
Archivo modificado     —> Hash: f29e88b7ac12...
Aunque el cambio sea mínimo, el hash cambia por completo.`
    },

    {
      heading: "3. Hashing para privacidad y ciberseguridad",
      text: [
        "El hashing es uno de los **métodos más seguros para proteger contraseñas** e información confidencial.",
        "En lugar de guardar la contraseña real, los sistemas almacenan su hash. Así, si alguien roba la base de datos, no obtiene las contraseñas, sino solo sus hashes irreversibles."
      ],
      list: [
        "🔹 Las contraseñas no se almacenan directamente.",
        "🔹 Los sistemas guardan solo su hash (ej. SHA-256, bcrypt, Argon2).",
        "🔹 Si una base de datos es robada, no es posible ver las contraseñas originales.",
        "🔹 Se usa también para detectar malware comparando hashes de archivos infectados."
      ]
    },

    {
      heading: "Hashing y detección de malware",
      text: [
        "Los sistemas de ciberseguridad usan hashing para detectar software malicioso.",
        "Cada archivo limpio tiene un hash conocido. Si un archivo tiene un hash diferente, puede ser una versión corrupta o infectada con malware.",
        "Así funcionan los antivirus modernos: comparan el hash de un archivo con una lista de hashes de archivos maliciosos conocidos."
      ],
      list: [
        "🔹 Detecta si un archivo ha sido manipulado.",
        "🔹 Permite identificar versiones maliciosas de un programa.",
        "🔹 Es usado en herramientas como VirusTotal o bases de datos de amenazas."
      ]
    },

    {
      heading: "Hashing como herramienta de confianza",
      text: [
        "Gracias a todas estas capacidades, el hashing se ha convertido en **una herramienta indispensable para garantizar integridad, autenticidad y confianza digital**.",
        "Sin hashing, tecnologías como blockchain, sistemas de contraseñas, firmas digitales, autenticación de documentos y protección de archivos no serían posibles."
      ],
      list: [
        "🔹 Protege datos sensibles y documentos importantes.",
        "🔹 Asegura transacciones bancarias y blockchain.",
        "🔹 Es la base de la verificación digital y autenticación.",
        "🔹 Permite detectar manipulaciones, errores y fraudes."
      ]
    },

    {
      heading: "Resumen final del capítulo",
      list: [
        "🔹 El hashing garantiza que los datos no sean alterados sin ser detectados.",
        "🔹 Protege contraseñas, archivos, transacciones y bloques.",
        "🔹 Se usa en criptomonedas, bases de datos, antivirus y autenticaciones.",
        "🔹 Es imposible revertir un hash para conocer la información original.",
        "🔹 Es una pieza fundamental para la seguridad digital y blockchain."
      ]
    },

    {
      heading: "¿Qué viene ahora?",
      text: [
        "En el próximo capítulo aprenderás sobre **Firmas Digitales (II)**: cómo se usan las claves públicas y privadas para firmar una transacción y cómo una billetera demuestra que tú eres el verdadero dueño sin mostrar tu clave privada.",
        "Con esto, completamos el puente entre **hashing → firmas digitales → seguridad e identidad en blockchain**."
      ]
    }
  ]
};

export default CAP18;
